import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"

const API_LOG_ENDPOINT = "https://ats.bharatpayroll.com/qxbox/api/v1/api-log/"
const PAGE_SIZE = 10

const methodColors = {
  GET: { bg: "#e0f2fe", color: "#0369a1" },
  POST: { bg: "#ecfdf5", color: "#047857" },
  PUT: { bg: "#fff7ed", color: "#c2410c" },
  PATCH: { bg: "#f5f3ff", color: "#6d28d9" },
  DELETE: { bg: "#fef2f2", color: "#b91c1c" },
}

const getToken = () => {
  try {
    const authUser = localStorage.getItem("authUser")
    if (authUser) {
      const parsedAuthUser = JSON.parse(authUser)
      return parsedAuthUser?.access || parsedAuthUser?.token
    }
  } catch {
    return localStorage.getItem("accessToken") || localStorage.getItem("token")
  }

  return localStorage.getItem("accessToken") || localStorage.getItem("token")
}

const formatDate = value => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const formatJson = value => {
  if (value === null || value === undefined || value === "") return "No data"
  try {
    return typeof value === "string"
      ? JSON.stringify(JSON.parse(value), null, 2)
      : JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const getStatusMeta = status => {
  const code = Number(status)
  if (code >= 200 && code < 300) return { label: code, bg: "#dcfce7", color: "#166534" }
  if (code >= 300 && code < 400) return { label: code, bg: "#fef9c3", color: "#854d0e" }
  if (code >= 400) return { label: code, bg: "#fee2e2", color: "#991b1b" }
  return { label: status || "-", bg: "#f1f5f9", color: "#475569" }
}

const Badge = ({ children, meta }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 54,
      padding: "4px 10px",
      borderRadius: 999,
      background: meta.bg,
      color: meta.color,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1,
    }}
  >
    {children}
  </span>
)

const IconButton = ({ children, disabled, onClick, active }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      minWidth: 34,
      height: 34,
      padding: "0 10px",
      border: active ? "1px solid #0b5f95" : "1px solid #dbe3ec",
      borderRadius: 6,
      background: active ? "#0b5f95" : "#ffffff",
      color: active ? "#ffffff" : "#334155",
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.45 : 1,
    }}
  >
    {children}
  </button>
)

const ApiLogs = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [search, setSearch] = useState("")
  const [methodFilter, setMethodFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")
  const [selectedLog, setSelectedLog] = useState(null)
  const [activeTab, setActiveTab] = useState("request")
  const [copied, setCopied] = useState(false)

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const fetchLogs = async nextPage => {
    setLoading(true)
    setError("")

    try {
      const token = getToken()
      const response = await axios.get(API_LOG_ENDPOINT, {
        params: { page: nextPage },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })

      setLogs(response.data?.results || [])
      setTotalCount(response.data?.count || 0)
      setPage(nextPage)
    } catch (err) {
      setLogs([])
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Unable to load API logs. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLogs(1)
  }, [])

  useEffect(() => {
    if (!selectedLog) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [selectedLog])

  const visibleLogs = useMemo(() => {
    return logs.filter(log => {
      const status = Number(log.status_code)
      const haystack = [
        log.api_name,
        log.url,
        log.method,
        log.status_code,
        log.error_message,
      ]
        .join(" ")
        .toLowerCase()

      if (search && !haystack.includes(search.toLowerCase())) return false
      if (methodFilter !== "all" && log.method !== methodFilter) return false
      if (statusFilter === "success" && !(status >= 200 && status < 300)) return false
      if (statusFilter === "failed" && !(status >= 400)) return false
      return true
    })
  }, [logs, methodFilter, search, statusFilter])

  const methods = useMemo(
    () => [...new Set(logs.map(log => log.method).filter(Boolean))],
    [logs]
  )

  const pageStats = useMemo(() => {
    const success = logs.filter(log => {
      const code = Number(log.status_code)
      return code >= 200 && code < 300
    }).length
    const failed = logs.filter(log => Number(log.status_code) >= 400).length
    return {
      total: totalCount,
      pageTotal: logs.length,
      success,
      failed,
      rate: logs.length ? Math.round((success / logs.length) * 100) : 0,
    }
  }, [logs, totalCount])

  const paginationItems = useMemo(() => {
    const pages = []
    const start = Math.max(1, page - 2)
    const end = Math.min(totalPages, page + 2)

    if (start > 1) pages.push(1)
    if (start > 2) pages.push("...")
    for (let i = start; i <= end; i += 1) pages.push(i)
    if (end < totalPages - 1) pages.push("...")
    if (end < totalPages) pages.push(totalPages)
    return pages
  }, [page, totalPages])

  const openLog = log => {
    setSelectedLog(log)
    setActiveTab("request")
    setCopied(false)
  }

  const copyContent = value => {
    navigator.clipboard?.writeText(formatJson(value)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const currentJson =
    activeTab === "request"
      ? selectedLog?.request_payload
      : activeTab === "response"
      ? selectedLog?.response_body
      : selectedLog?.error_message || "No error message"

  return (
    <div className="page-content" style={{ background: "#f6f8fb" }}>
      <div style={{ padding: "20px 24px 28px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <div style={{ color: "#64748b", fontSize: 13, fontWeight: 600 }}>
            Monitoring
          </div>
          <h1 style={{ margin: "4px 0 6px", color: "#0f172a", fontSize: 24 }}>
            API Logs
          </h1>
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Track requests, response status and payload details from the API log service.
          </div>
        </div>

        <button
          type="button"
          onClick={() => fetchLogs(page)}
          disabled={loading}
          style={{
            border: "1px solid #0b5f95",
            background: "#0b5f95",
            color: "#ffffff",
            borderRadius: 6,
            padding: "9px 16px",
            fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Refreshing..." : "Refresh Logs"}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 14,
          marginBottom: 16,
        }}
      >
        {[
          ["Total Logs", pageStats.total.toLocaleString(), "#0b5f95"],
          ["Current Page", pageStats.pageTotal, "#475569"],
          ["Successful", pageStats.success, "#15803d"],
          ["Failed", pageStats.failed, "#b91c1c"],
          ["Page Success Rate", `${pageStats.rate}%`, "#7c3aed"],
        ].map(([label, value, color]) => (
          <div
            key={label}
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                color: "#64748b",
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {label}
            </div>
            <div style={{ color, fontSize: 26, fontWeight: 800, marginTop: 6 }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          padding: 14,
          marginBottom: 16,
          display: "grid",
          gridTemplateColumns: "1fr 180px 180px",
          gap: 12,
        }}
      >
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search API name, URL, method, status..."
          style={{
            border: "1px solid #dbe3ec",
            borderRadius: 6,
            padding: "9px 12px",
            color: "#0f172a",
            outline: "none",
          }}
        />
        <select
          value={methodFilter}
          onChange={e => setMethodFilter(e.target.value)}
          style={{
            border: "1px solid #dbe3ec",
            borderRadius: 6,
            padding: "9px 12px",
            color: "#0f172a",
            background: "#ffffff",
          }}
        >
          <option value="all">All Methods</option>
          {methods.map(method => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            border: "1px solid #dbe3ec",
            borderRadius: 6,
            padding: "9px 12px",
            color: "#0f172a",
            background: "#ffffff",
          }}
        >
          <option value="all">All Status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {["ID", "API Name", "Method", "Status", "URL", "Created At", "Action"].map(
                  heading => (
                    <th
                      key={heading}
                      style={{
                        padding: "12px 14px",
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 800,
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {heading}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: 42, textAlign: "center", color: "#64748b" }}>
                    Loading API logs...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} style={{ padding: 42, textAlign: "center", color: "#b91c1c" }}>
                    {error}
                  </td>
                </tr>
              ) : visibleLogs.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 42, textAlign: "center", color: "#64748b" }}>
                    No logs found.
                  </td>
                </tr>
              ) : (
                visibleLogs.map(log => {
                  const methodMeta =
                    methodColors[log.method] || { bg: "#f1f5f9", color: "#334155" }
                  const statusMeta = getStatusMeta(log.status_code)
                  return (
                    <tr key={log.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "12px 14px", color: "#64748b", fontWeight: 700 }}>
                        #{log.id}
                      </td>
                      <td style={{ padding: "12px 14px", color: "#0f172a", fontWeight: 700 }}>
                        {log.api_name || "-"}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <Badge meta={methodMeta}>{log.method || "-"}</Badge>
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <Badge meta={statusMeta}>{statusMeta.label}</Badge>
                      </td>
                      <td
                        title={log.url}
                        style={{
                          padding: "12px 14px",
                          color: "#475569",
                          maxWidth: 360,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {log.url || "-"}
                      </td>
                      <td style={{ padding: "12px 14px", color: "#475569", whiteSpace: "nowrap" }}>
                        {formatDate(log.created_at)}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <button
                          type="button"
                          onClick={() => openLog(log)}
                          style={{
                            border: "1px solid #cbd5e1",
                            background: "#ffffff",
                            color: "#0b5f95",
                            borderRadius: 6,
                            padding: "6px 12px",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: "12px 14px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Page {page} of {totalPages} • {totalCount.toLocaleString()} records
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <IconButton disabled={page <= 1 || loading} onClick={() => fetchLogs(page - 1)}>
              Prev
            </IconButton>
            {paginationItems.map((item, index) =>
              item === "..." ? (
                <span key={`ellipsis-${index}`} style={{ color: "#94a3b8", padding: "0 4px" }}>
                  ...
                </span>
              ) : (
                <IconButton
                  key={item}
                  active={item === page}
                  disabled={loading}
                  onClick={() => fetchLogs(item)}
                >
                  {item}
                </IconButton>
              )
            )}
            <IconButton
              disabled={page >= totalPages || loading}
              onClick={() => fetchLogs(page + 1)}
            >
              Next
            </IconButton>
          </div>
        </div>
      </div>

      {selectedLog && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            zIndex: 1050,
            display: "flex",
            justifyContent: "flex-end",
            overflow: "hidden",
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: "min(760px, 100%)",
              height: "100vh",
              maxHeight: "100vh",
              background: "#ffffff",
              boxShadow: "-20px 0 50px rgba(15, 23, 42, 0.2)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "18px 22px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
              }}
            >
              <div>
                <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                  LOG #{selectedLog.id}
                </div>
                <h2 style={{ margin: "4px 0", color: "#0f172a", fontSize: 20 }}>
                  {selectedLog.api_name || "API Log Details"}
                </h2>
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  {formatDate(selectedLog.created_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                style={{
                  border: "1px solid #dbe3ec",
                  background: "#ffffff",
                  borderRadius: 6,
                  width: 34,
                  height: 34,
                  cursor: "pointer",
                  color: "#475569",
                  fontSize: 18,
                }}
              >
                x
              </button>
            </div>

            <div
              style={{
                padding: 22,
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 12,
                  marginBottom: 18,
                }}
              >
                {[
                  ["Method", selectedLog.method],
                  ["Status", selectedLog.status_code],
                  ["Success", selectedLog.success ? "Yes" : "No"],
                  ["Updated", formatDate(selectedLog.updated_at)],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <div style={{ color: "#64748b", fontSize: 11, fontWeight: 800 }}>
                      {label}
                    </div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 14,
                        fontWeight: 800,
                        marginTop: 5,
                        wordBreak: "break-word",
                      }}
                    >
                      {value || "-"}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 18,
                  color: "#334155",
                  wordBreak: "break-all",
                }}
              >
                <strong>URL:</strong> {selectedLog.url || "-"}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  borderBottom: "1px solid #e2e8f0",
                  marginBottom: 14,
                }}
              >
                {["request", "response", "error"].map(tab => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab)
                      setCopied(false)
                    }}
                    style={{
                      border: "none",
                      borderBottom:
                        activeTab === tab ? "3px solid #0b5f95" : "3px solid transparent",
                      background: "transparent",
                      color: activeTab === tab ? "#0b5f95" : "#64748b",
                      padding: "10px 14px",
                      fontWeight: 800,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={() => copyContent(currentJson)}
                  style={{
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#334155",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>

              <pre
                style={{
                  margin: 0,
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  background: "#0f172a",
                  color: "#e2e8f0",
                  borderRadius: 8,
                  padding: 16,
                  fontSize: 12,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {formatJson(currentJson)}
              </pre>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default ApiLogs
